/**
 * Marca do Livra — "Fita em L" (aprovada pelo fundador em 07/10/2026).
 *
 * O marcador desce do topo e dobra em 90° formando o L; ao lado, "livra" em
 * Bricolage 800 já convertido em curva (não depende de fonte carregada, então
 * a marca aparece igual no boot, antes de qualquer webfont).
 *
 * Fonte única do desenho: `livraMark.json` (gerado do kit em
 * `redesign-livra/logo/final/`). Os scripts de assets e o SSR das functions
 * leem a mesma geometria.
 *
 * ⚠️ As cores da FITA e do papel do ícone são da marca, não do tema: não trocar
 * por token semântico (a marca não muda com o tema). A PALAVRA usa
 * `currentColor`, então herda a cor de texto de quem a usa.
 */
import mark from './livraMark.json';

const { colors, symbol, icon, word, lockup } = mark;

type Size = { className?: string; style?: React.CSSProperties };

/** A fita sozinha, sem placa. Altura manda; largura segue a proporção 54:84. */
export function LivraSymbol({ className, style }: Size) {
  return (
    <svg viewBox={symbol.viewBox} className={className} style={style} aria-hidden="true" focusable="false">
      <path d={symbol.front} fill={colors.ribbon} />
      <path d={symbol.back} fill={colors.ribbonBack} />
    </svg>
  );
}

/** Desenho do ícone (placa papel + fita) dentro de um viewBox 100×100. */
function IconArt({ edge }: { edge: boolean }) {
  return (
    <>
      <rect width="100" height="100" rx={icon.radius} fill={colors.paper} />
      {/* Contorno sutil: sem ele, a placa papel some em fundo claro. Fica
          ABAIXO da fita, para a fita continuar saindo da borda. */}
      {edge && (
        <rect x="1" y="1" width="98" height="98" rx={icon.radius - 1} fill="none"
          stroke="currentColor" strokeOpacity="0.14" strokeWidth="1.4" />
      )}
      <path d={symbol.front} fill={colors.ribbon} />
      <path d={symbol.back} fill={colors.ribbonBack} />
    </>
  );
}

/** O ícone do app (placa papel arredondada + fita). */
export function LivraIcon({ className, style, edge = true }: Size & { edge?: boolean }) {
  return (
    <svg viewBox={icon.viewBox} className={className} style={style} aria-hidden="true" focusable="false">
      <IconArt edge={edge} />
    </svg>
  );
}

/** Só a palavra "livra" (em curva, `currentColor`). */
export function LivraWordmark({ className, style }: Size) {
  return (
    <svg viewBox={word.viewBox} className={className} style={style} aria-hidden="true" focusable="false">
      <path d={word.path} fill="currentColor" />
    </svg>
  );
}

/**
 * Rótulo só para leitor de tela — em estilo INLINE, não `sr-only`: a marca
 * também entra nos cards exportáveis, que são capturados por `html-to-image`
 * e não podem depender de classe do Tailwind (ver skill share-studio).
 */
const HIDDEN_LABEL: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

const LOCKUP_W = lockup.tile + lockup.gap + word.width * lockup.wordScale;
const WORD_Y = lockup.wordBaseline - word.ascender * lockup.wordScale;

/**
 * Assinatura: ícone + "livra". O tamanho vem da ALTURA (`height`, em px — é a
 * altura da placa do ícone); a largura acompanha (~2,9× a altura). Carrega o
 * nome "Livra" em texto acessível — leitores de tela e testes encontram
 * "Livra" sem depender do desenho.
 */
export function LivraLogo({
  className,
  style,
  height = 32,
  edge = true,
}: Size & { height?: number; edge?: boolean }) {
  return (
    <span className={className} style={{ position: 'relative', display: 'inline-flex', lineHeight: 0, ...style }}>
      <svg
        viewBox={`0 0 ${LOCKUP_W.toFixed(1)} ${lockup.tile}`}
        height={height}
        width={(height * LOCKUP_W) / lockup.tile}
        style={{ overflow: 'visible' }}
        aria-hidden="true"
        focusable="false"
      >
        <svg width={lockup.tile} height={lockup.tile} viewBox={icon.viewBox}>
          <IconArt edge={edge} />
        </svg>
        <path
          d={word.path}
          fill="currentColor"
          transform={`translate(${lockup.tile + lockup.gap} ${WORD_Y.toFixed(2)}) scale(${lockup.wordScale})`}
        />
      </svg>
      <span style={HIDDEN_LABEL}>Livra</span>
    </span>
  );
}
